import { useTranslation } from 'react-i18next';

import { Alert } from '../../design/primitives/Alert';

/**
 * O que uma listagem esta dizendo — uma coisa de cada vez.
 *
 * Oito telas escreviam a mesma soma de dois estados:
 *
 * ```tsx
 * {list.isError ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}
 * {items.length === 0 && !list.isPending ? <p>{t('...empty')}</p> : null}
 * ```
 *
 * Em erro, `isPending` e falso e `items` e `[]`, entao os DOIS renderizam: a
 * pessoa le "nao foi possivel carregar" e, logo abaixo, "voce nao tem
 * gravacoes". A segunda frase e uma afirmacao sobre o acervo que ninguem
 * verificou, e e a que fica na memoria — o mesmo defeito que `panelState`
 * selou na home ("erro primeiro, porque a resposta que nao veio nao e uma
 * resposta vazia") e que `listState` selou na presenca.
 *
 * A correcao recusada foi acrescentar `&& !isError` em cada uma das oito: sao
 * oito oportunidades de esquecer, e o esquecimento nao quebra nada visivel —
 * ele so mente. Aqui a combinacao e inexprimivel: quem chama entrega os tres
 * fatos e nao escolhe qual frase sai.
 *
 * O componente NAO desenha a lista. Ela continua na tela que a possui, porque
 * cada uma monta a sua de um jeito — `ul`, `table`, `nav` — e uma abstracao que
 * tentasse cobrir as tres viraria um segundo lugar onde a marcacao se decide.
 */
export interface ListOutcomeProps {
  /** A consulta ainda nao respondeu nada. */
  readonly isPending: boolean;
  readonly isError: boolean;
  /** Quantos itens a resposta trouxe — depois de qualquer recorte da tela. */
  readonly count: number;
  /**
   * A frase do vazio. Obrigatoria: lista que pode ficar vazia sem ter o que
   * dizer deixa um retangulo mudo, e retangulo mudo se le como carga travada.
   */
  readonly empty: string;
}

export function ListOutcome({ isPending, isError, count, empty }: ListOutcomeProps) {
  const { t } = useTranslation();

  // A ordem e o conteudo da regra, e nao arrumacao: erro antes de pendente
  // porque com `retry: false` a consulta que falhou nao esta mais carregando, e
  // erro antes de vazio porque a resposta que nao veio nao e uma resposta
  // vazia.
  if (isError) return <Alert tone="error">{t('error.loadFailed')}</Alert>;
  if (isPending || count > 0) return null;
  return <p>{empty}</p>;
}
